import {
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { MouseEvent, ReactNode } from 'react';

type SidebarItemProps = {
    icon: ReactNode;
    label: string;
    secondaryLabel?: string;
    isNarrowBar: boolean;
    selected?: boolean;
    disabled?: boolean;
    hasPopup?: boolean;
    expanded?: boolean;
    onClick: (event: MouseEvent<HTMLElement>) => void;
    'data-testid'?: string;
};

export const SidebarItem = ({
    icon,
    label,
    secondaryLabel,
    isNarrowBar,
    selected,
    disabled,
    hasPopup,
    expanded,
    onClick,
    'data-testid': dataTestId,
}: SidebarItemProps) => {
    const theme = useTheme();
    const white = theme.palette.common.white;
    const inheritIconColor = {
        '& .MuiListItemIcon-root': { color: 'inherit' },
    };

    return (
        <Tooltip title={isNarrowBar ? label : ''} placement='right'>
            <ListItemButton
                onClick={onClick}
                selected={selected}
                disabled={disabled}
                aria-label={isNarrowBar ? label : undefined}
                aria-haspopup={hasPopup}
                aria-expanded={expanded}
                data-testid={dataTestId}
                sx={{
                    minHeight: 40,
                    mb: 0.25,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    justifyContent: isNarrowBar ? 'center' : 'flex-start',
                    color: alpha(white, 0.78),
                    ...inheritIconColor,
                    '&:hover': {
                        backgroundColor: alpha(white, 0.08),
                        color: white,
                        ...inheritIconColor,
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: alpha(white, 0.14),
                        color: white,
                        ...inheritIconColor,
                    },
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: isNarrowBar ? 0 : 32,
                        '& .MuiSvgIcon-root': { fontSize: 20 },
                    }}
                >
                    {icon}
                </ListItemIcon>
                {!isNarrowBar && (
                    <ListItemText
                        primary={label}
                        secondary={secondaryLabel}
                        primaryTypographyProps={{ noWrap: true }}
                        secondaryTypographyProps={{ noWrap: true }}
                        sx={{
                            my: 0,
                            '& .MuiListItemText-primary': {
                                color: 'inherit',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                            },
                            '& .MuiListItemText-secondary': {
                                color: alpha(white, 0.6),
                                fontSize: '0.75rem',
                            },
                        }}
                    />
                )}
            </ListItemButton>
        </Tooltip>
    );
};
